import { useState, useEffect, useCallback } from 'react';
import { ThumbsUp, ThumbsDown } from 'lucide-react';
import { cn } from '@/lib/utils';
import { triggerHaptic } from '@/hooks/useHaptics';
import { motion } from 'framer-motion';

interface ImageThumbsVotingProps {
  imageId: string;
  votes: { thumbsUp: number; thumbsDown: number };
  userVote?: 'up' | 'down' | null;
  onVote: (imageId: string, voteType: 'up' | 'down') => Promise<boolean> | void;
  disabled?: boolean;
  resultsOnly?: boolean;
  /** @deprecated No longer used - boosted votes are now blended into the votes prop directly */
  displayTotal?: number;
}

/**
 * Elegant thumbs up/down voting overlay for poll images.
 * Always shows results - users can see votes without voting first.
 */
export function ImageThumbsVoting({
  imageId,
  votes,
  userVote: existingVote = null,
  onVote,
  disabled = false,
  resultsOnly = false,
  displayTotal,
}: ImageThumbsVotingProps) {
  const [localUserVote, setLocalUserVote] = useState<'up' | 'down' | null>(existingVote);
  const [localVotes, setLocalVotes] = useState(votes);
  const [isVoting, setIsVoting] = useState(false);

  // CRITICAL: Reset local state when imageId changes (for carousel swiping)
  useEffect(() => {
    setLocalUserVote(existingVote);
    setLocalVotes(votes);
  }, [imageId, existingVote, votes]);

  const handleVote = useCallback(async (voteType: 'up' | 'down', e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();

    console.debug('[ImageThumbsVoting] click', {
      imageId,
      voteType,
      localUserVote,
      isVoting,
      disabled,
      existingVote,
    });
    
    // If clicking the same vote, do nothing; otherwise allow vote change
    if (localUserVote === voteType || isVoting || disabled) return;
    
    triggerHaptic('medium');
    setIsVoting(true);

    const previousVote = localUserVote;

    // Optimistically update UI
    setLocalUserVote(voteType);
    setLocalVotes(prev => {
      const updated = { ...prev };
      // Remove previous vote if changing
      if (previousVote) {
        if (previousVote === 'up') updated.thumbsUp = Math.max(0, updated.thumbsUp - 1);
        if (previousVote === 'down') updated.thumbsDown = Math.max(0, updated.thumbsDown - 1);
      }
      // Add new vote
      if (voteType === 'up') updated.thumbsUp += 1;
      else updated.thumbsDown += 1;
      return updated;
    });

    try {
      const result = await onVote(imageId, voteType);

      console.debug('[ImageThumbsVoting] onVote result', {
        imageId,
        voteType,
        result,
      });
      
      // If vote failed, revert optimistic update
      if (result === false) {
        setLocalUserVote(previousVote);
        setLocalVotes(prev => {
          const reverted = { ...prev };
          if (voteType === 'up') reverted.thumbsUp = Math.max(0, reverted.thumbsUp - 1);
          else reverted.thumbsDown = Math.max(0, reverted.thumbsDown - 1);
          if (previousVote) {
            if (previousVote === 'up') reverted.thumbsUp += 1;
            else reverted.thumbsDown += 1;
          }
          return reverted;
        });
      }
    } catch (err) {
      // Revert on error
      setLocalUserVote(previousVote);
      setLocalVotes(prev => {
        const reverted = { ...prev };
        if (voteType === 'up') reverted.thumbsUp = Math.max(0, reverted.thumbsUp - 1);
        else reverted.thumbsDown = Math.max(0, reverted.thumbsDown - 1);
        if (previousVote) {
          if (previousVote === 'up') reverted.thumbsUp += 1;
          else reverted.thumbsDown += 1;
        }
        return reverted;
      });
    } finally {
      setIsVoting(false);
    }
  }, [imageId, localUserVote, isVoting, disabled, onVote, existingVote]);

  const total = localVotes.thumbsUp + localVotes.thumbsDown;
  const upPercent = total > 0 ? Math.round((localVotes.thumbsUp / total) * 100) : 50;
  const hasVoted = localUserVote !== null;

  // Results-only view for own polls
  if (resultsOnly) {
    return (
      <div className="flex items-center justify-center gap-3 w-full px-4 py-2 bg-black/30 backdrop-blur-sm">
        {total > 0 ? (
          <>
            <div className="flex items-center gap-1.5 text-white/90">
              <ThumbsDown className="w-4 h-4" />
              <span className="text-sm font-semibold">{100 - upPercent}%</span>
            </div>
            <span className="text-xs text-white/50">·</span>
            <span className="text-xs font-medium text-white/60">
              {total} {total === 1 ? 'vote' : 'votes'}
            </span>
            <span className="text-xs text-white/50">·</span>
            <div className="flex items-center gap-1.5 text-white/90">
              <ThumbsUp className="w-4 h-4" />
              <span className="text-sm font-semibold">{upPercent}%</span>
            </div>
          </>
        ) : (
          <span className="text-sm text-white/60">No votes yet</span>
        )}
      </div>
    );
  }

  return (
    <div className="absolute inset-x-0 bottom-0 z-20 pointer-events-auto" onTouchStart={(e) => e.stopPropagation()}>
      <div className="flex items-end justify-center gap-6 p-4">
        {/* Thumbs Down (left) */}
        <button
          onClick={(e) => handleVote('down', e)}
          disabled={isVoting || disabled || localUserVote === 'down'}
          className={cn(
            "flex flex-col items-center gap-1.5 transition-all",
            hasVoted && localUserVote === 'down'
              ? "scale-110"
              : hasVoted && localUserVote === 'up'
              ? "opacity-60 active:opacity-100 active:scale-95"
              : "active:scale-95"
          )}
        >
          <div className={cn(
            "w-14 h-14 rounded-full flex items-center justify-center backdrop-blur-md transition-all",
            hasVoted && localUserVote === 'down'
              ? "bg-rose-500/80 text-white shadow-lg shadow-rose-500/30"
              : hasVoted
              ? "bg-black/20 text-white/60 border border-white/10"
              : "bg-black/30 text-white border border-white/15 active:bg-black/50"
          )}>
            <ThumbsDown className="w-6 h-6" />
          </div>
        </button>

        {/* Thumbs Up (right) */}
        <button
          onClick={(e) => handleVote('up', e)}
          disabled={isVoting || disabled || localUserVote === 'up'}
          className={cn(
            "flex flex-col items-center gap-1.5 transition-all",
            hasVoted && localUserVote === 'up'
              ? "scale-110"
              : hasVoted && localUserVote === 'down'
              ? "opacity-60 active:opacity-100 active:scale-95"
              : "active:scale-95"
          )}
        >
          <div className={cn(
            "w-14 h-14 rounded-full flex items-center justify-center backdrop-blur-md transition-all",
            hasVoted && localUserVote === 'up'
              ? "bg-emerald-500/80 text-white shadow-lg shadow-emerald-500/30"
              : hasVoted
              ? "bg-black/20 text-white/60 border border-white/10"
              : "bg-black/30 text-white border border-white/15 active:bg-black/50"
          )}>
            <ThumbsUp className="w-6 h-6" />
          </div>
        </button>
      </div>
    </div>
  );
}
